import { Alert, Button, capitalize } from "@mui/material";
import { buildPermission } from "portal/utils/auth";
import { CarbonDataGrid } from "portal/components/CarbonDataGrid";
import { ConfirmationDialog } from "portal/components/ConfirmationDialog";
import {
  CursorFetchArgs,
  CursorPageResponse,
  useCursorDataSource,
} from "portal/utils/hooks/useCursorDataSource";
import { DEBOUNCE_MS } from "../../utils/timing";
import {
  emptyRowSelectionModel,
  hasRowSelection,
  rowSelectionModelToArray,
} from "portal/utils/gridRowSelection";
import { FeatureFlag } from "portal/utils/hooks/useFeatureFlag";
import { findWhere } from "portal/utils/arrays";
import { formatList, titleCase } from "portal/utils/strings";
import {
  getReportInstancesPath,
  getReportPath,
  Slug,
} from "portal/utils/routing";
import {
  GridActionsCellItem,
  GridColDef,
  GridRowSelectionModel,
} from "@mui/x-data-grid-premium";
import { Link, useNavigate } from "react-router-dom";
import { NoScroll } from "portal/components/Page";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import { portalApi } from "portal/state/portalApi";
import { ReportResponse } from "protos/portal/reports";
import { SearchField } from "portal/components/header/SearchField";
import { useAuthorizationRequired } from "portal/components/auth/WithAuthorizationRequired";
import { useDebounce } from "@uidotdev/usehooks";
import {
  useLazyPopups,
  useMutationPopups,
} from "portal/utils/hooks/useApiPopups";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import { withFeatureFlag } from "portal/components/RequireFeatureFlag";
import AddIcon from "@mui/icons-material/AddOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import React, {
  FunctionComponent,
  useCallback,
  useMemo,
  useState,
} from "react";
import RunsIcon from "@mui/icons-material/TocOutlined";

const _ReportList: FunctionComponent = () => {
  const { user, customer } = useSelf();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [getReports] = useLazyPopups(
    portalApi.endpoints.listReports.useLazyQuery()
  );

  const [searchInputValue, setSearchInputValue] = useState("");
  const searchText = useDebounce(searchInputValue.trim(), DEBOUNCE_MS);
  const fetchReports = useCallback(
    async ({
      cursor,
      limit,
    }: CursorFetchArgs): Promise<CursorPageResponse<ReportResponse>> => {
      const { data } = await getReports({
        cursor,
        limit,
        search: searchText.length > 0 ? searchText : undefined,
      });
      return {
        rows: data?.reports ?? [],
        pageInfo: {
          hasNextPage: Boolean(data?.pagination?.nextOld),
          nextCursor: data?.pagination?.nextOld,
        },
      };
    },
    [getReports, searchText]
  );
  const {
    dataGridProps,
    fetchedRows: reports,
    reset,
  } = useCursorDataSource<ReportResponse>(fetchReports, {
    initialPageSize: 50,
  });
  const canUpdateSelf = useAuthorizationRequired(
    buildPermission(
      PermissionAction.update,
      PermissionResource.reports,
      PermissionDomain.self
    )
  );
  const canUpdateCustomer = useAuthorizationRequired(
    buildPermission(
      PermissionAction.update,
      PermissionResource.reports,
      PermissionDomain.customer
    )
  );
  const canUpdateAll = useAuthorizationRequired(
    buildPermission(
      PermissionAction.update,
      PermissionResource.reports,
      PermissionDomain.all
    )
  );
  const canReadCustomers = useAuthorizationRequired(
    buildPermission(
      PermissionAction.read,
      PermissionResource.customers,
      PermissionDomain.all
    )
  );

  const [deleteReport, { isLoading: isDeleteLoading }] = useMutationPopups(
    portalApi.endpoints.deleteReport.useMutation(),
    {
      success: capitalize(
        t("utils.actions.deletedLong", {
          subject: t("models.reports.report_one"),
        })
      ),
    }
  );
  // confirmation dialog
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);

  const [selectedSlugs, setSelectedSlugs] = useState<GridRowSelectionModel>(
    emptyRowSelectionModel()
  );

  const columns: readonly GridColDef<ReportResponse>[] = useMemo(
    () => [
      {
        disableColumnMenu: true,
        field: "name",
        type: "string",
        headerName: t("models.reports.fields.name"),
        headerClassName: "capitalize",
        renderCell: ({ row: report }) => (
          <Link
            to={getReportPath(report.slug)}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {report.name}
          </Link>
        ),
      },
      {
        disableColumnMenu: true,
        field: "authorId",
        type: "string",
        headerName: t("models.reports.fields.authorId"),
        valueGetter: (_value, report) => report.authorName,
        headerClassName: "capitalize",
      },
      {
        disableColumnMenu: true,
        field: "customerId",
        type: "string",
        headerName: t("models.customers.customer_one"),
        valueGetter: (_value, report) => report.customer?.name,
        headerClassName: "capitalize",
      },
      {
        disableColumnMenu: true,
        field: "automateWeekly",
        type: "string",
        headerName: t("models.reports.fields.automateWeekly.name"),
        valueFormatter: (automateWeekly) =>
          // idk why valueFormatter's value is never because it definitely is the result of valueGetter
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          automateWeekly
            ? t("models.reports.fields.automateWeekly.values.weekly")
            : "",
        headerClassName: "capitalize",
      },
      {
        disableColumnMenu: true,
        type: "actions",
        field: "actions",
        headerName: titleCase(t("models.reportInstances.run_other")),
        getActions: ({ row: report }) => [
          <GridActionsCellItem
            key="runs"
            component={Link}
            icon={<RunsIcon />}
            label={t("utils.actions.viewLong", {
              subject: t("models.reportInstances.run_other"),
            })}
            // @ts-expect-error this is needed because we are passing component={Link} above
            to={getReportInstancesPath(report.slug)}
          />,
        ],
        headerClassName: "capitalize",
      },
    ],
    [t]
  );

  return (
    <NoScroll>
      <CarbonDataGrid<ReportResponse>
        {...dataGridProps}
        className="flex flex-1"
        getRowId={(report) => report.slug}
        getRowClassName={() => "cursor-pointer"}
        header={
          <>
            <SearchField
              value={searchInputValue}
              onChange={setSearchInputValue}
              label={t("utils.actions.searchLong", {
                subject: capitalize(
                  t("models.reports.report_one", {
                    count: reports.length,
                  })
                ),
              })}
            />
            <div className="flex gap-2">
              <Button
                variant="contained"
                color="error"
                loading={isDeleteLoading}
                disabled={!hasRowSelection(selectedSlugs)}
                onClick={() => setConfirmDelete(true)}
                startIcon={<DeleteIcon />}
              >
                <span className="ml-1 hidden md:inline">
                  {t("utils.actions.deleteLong", {
                    subject: t("utils.table.selected"),
                  })}
                </span>
                <span className="ml-1 md:hidden">
                  {t("utils.actions.delete")}
                </span>
              </Button>
              {confirmDelete &&
                (() => {
                  const selectedReports: ReportResponse[] = [];
                  const unownedReports: ReportResponse[] = [];
                  for (const slug of rowSelectionModelToArray(
                    selectedSlugs,
                    reports.map((report) => report.slug)
                  )) {
                    const report = findWhere(reports, { slug });
                    if (!report) {
                      continue;
                    }
                    const canUpdateReport =
                      (canUpdateSelf && report.authorId === user?.userId) ||
                      (canUpdateCustomer &&
                        report.customerId === customer?.db?.id) ||
                      canUpdateAll;
                    if (canUpdateReport) {
                      selectedReports.push(report);
                    } else {
                      unownedReports.push(report);
                    }
                  }
                  return (
                    <ConfirmationDialog
                      title={t("utils.actions.deleteLong", {
                        subject: capitalize(
                          t("models.reports.report_one", {
                            count: selectedReports.length,
                          })
                        ),
                      })}
                      description={
                        <>
                          {unownedReports.length > 0 && (
                            <Alert severity="warning">
                              {t(
                                "views.reports.scheduled.reportList.errors.unauthorized",
                                {
                                  subject: formatList(
                                    t,
                                    unownedReports.map(({ name }) => name)
                                  ),
                                }
                              )}
                            </Alert>
                          )}
                          <span>
                            {t(
                              "views.reports.scheduled.reportList.deleteConfirmationDescription",
                              {
                                list: formatList(
                                  t,
                                  selectedReports.map(({ name }) => name)
                                ),
                              }
                            )}
                          </span>
                        </>
                      }
                      destructive
                      yesText={t("utils.actions.deleteLong", {
                        subject: capitalize(
                          t("models.reports.report", {
                            count: selectedReports.length,
                          })
                        ),
                      })}
                      onClose={() => setConfirmDelete(false)}
                      onYes={async () => {
                        await Promise.all(
                          selectedReports.map(({ slug }) =>
                            deleteReport({ slug })
                          )
                        );
                        setSelectedSlugs(emptyRowSelectionModel());
                        reset();
                      }}
                    />
                  );
                })()}
              <Button
                component={Link}
                startIcon={<AddIcon />}
                to={getReportPath(Slug.NEW)}
              >
                <span className="ml-1 hidden md:inline">
                  {t("utils.actions.newLong", {
                    subject: capitalize(t("models.reports.report_one")),
                  })}
                </span>
                <span className="ml-1 md:hidden">{t("utils.actions.new")}</span>
              </Button>
            </div>
          </>
        }
        checkboxSelection
        keepNonExistentRowsSelected
        onRowSelectionModelChange={(newRowSelectionModel) =>
          setSelectedSlugs(newRowSelectionModel)
        }
        rowSelectionModel={selectedSlugs}
        onRowClick={({ row: report }) => navigate(getReportPath(report.slug))}
        columnVisibilityModel={{
          customerId: canReadCustomers,
          automateWeekly: canReadCustomers,
        }}
        columns={columns}
        disableRowSelectionOnClick
      />
    </NoScroll>
  );
};

export const ReportList = withAuthenticationRequired(
  withFeatureFlag(
    {
      flag: FeatureFlag.REPORTS,
      noFlag: "/",
    },
    _ReportList
  )
);
